import React, { useCallback, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, ChevronRight, CircleSlash, Package, RotateCcw, ShieldCheck, SlidersHorizontal, UserRound, Workflow, X } from 'lucide-react';
import { loadLaboratoryApprovals, loadLaboratoryWork, loadLaboratoryWorkflowVersion, reviewLaboratoryAcceptance, reviewLaboratoryStage } from './api';
import { useXomicsAuth } from './AuthContext';
import LaboratoryCollectionModal from './LaboratoryCollectionModal';
import { StageGlyph } from './LaboratoryGlyphs';
import LaboratoryStageForm, { RecordHistory, RecordValue } from './LaboratoryStageForm';
import { LabPagination, LabResource, TubeState, labError, labWhen, useLabSearch } from './LaboratoryParts';
import { fieldLabel, ownSampleTypeName, sampleTypeName, stageName, workflowName } from './LaboratoryText';
import { FilterMenu } from './MobileParts';
import { Empty, Failed, Head, Loading, Pill, SearchField } from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './laboratory-inventory.css';
import './laboratory-work.css';
import './laboratory-workflows.css';

const text = (key, values) => tx(`ui.lab.stage.${key}`, values);
const TONES = { completed: 'ok', stopped: 'bad', awaiting_approval: 'warn', awaiting_customer: 'warn', in_progress: 'info', planned: 'flat' };

function OrderStatus({ value }) {
  return <Pill tone={TONES[value] || 'flat'}>{value === 'completed' ? <Check aria-hidden="true" /> : value === 'awaiting_approval'
    ? <ShieldCheck aria-hidden="true" /> : value === 'awaiting_customer' ? <UserRound aria-hidden="true" />
      : value === 'stopped' ? <CircleSlash aria-hidden="true" /> : null}{text(`order.${value}`)}</Pill>;
}

// A customer's sample: after its first quality check the customer says whether we go on. Waiting
// for them, or their answer (theirs, or one we recorded for them).
function CustomerSay({ customer }) {
  if (customer === null) return null;
  if (customer.waiting) {
    return <p className="xo-work-help xo-work-customer"><UserRound aria-hidden="true" />{text('waiting_customer')}
      <Link to="/xomics/laboratory/customer-submissions">{text('customer_submissions')}</Link></p>;
  }
  if (customer.answer === null) return null;
  const { answer } = customer;
  return <p className="xo-work-help xo-work-customer"><UserRound aria-hidden="true" />
    {text(answer.answer === 'proceed' ? 'customer_go_on' : 'customer_stop', { name: answer.by || '', date: labWhen(answer.decidedAt) })}
    {!answer.byCustomer && <small>{text('customer_for_them')}</small>}{answer.note && <q>{answer.note}</q>}</p>;
}

// The stages of one work order, loops shown as attempts, and the current form.
// The tube is named only in a collection of several tubes: a single tube
// carries the code already on the card.
function WorkOrder({ order, collectionCode, onChanged }) {
  const [selectedId, setSelectedId] = useState(null);
  const loadDefinition = useCallback(() => loadLaboratoryWorkflowVersion(order.workflow.versionId), [order.workflow.versionId]);
  const definition = useLiveResource(['laboratory'], loadDefinition);
  const current = order.currentStage;
  const selected = order.stages.find(stage => stage.id === (selectedId === null ? current ? current.id : order.stages[order.stages.length - 1].id : selectedId));
  return <section className="xo-work-workflow xo-work-order" aria-label={`${order.workflow.code} ${order.tube.printedCode ?? order.tube.code}`}>
    <header><div><strong>{order.workflow.code}</strong><span>{workflowName(order.workflow.code)}
      {(order.tube.printedCode ?? order.tube.code) !== collectionCode && <><span aria-hidden="true"> · </span><code>{order.tube.printedCode ?? order.tube.code}</code></>}</span></div>
      <div><span className="xo-work-count">{tx('ui.lab.work.progress', { done: order.completedSteps, total: order.totalSteps })}</span><OrderStatus value={order.status} /></div>
    </header>
    <ol className="xo-work-stages">{order.stages.map(stage => <li key={stage.id} className={`is-${stage.state}`}>
      <button type="button" aria-pressed={selected !== undefined && selected.id === stage.id} onClick={() => setSelectedId(stage.id)}
        aria-label={`${stage.position}. ${stageName(stage.code)} · ${text(`state.${stage.state}`)}`}>
        <span className="xo-work-stage-symbol">{stage.state === 'completed' ? <Check aria-hidden="true" /> : stage.state === 'stopped'
          ? <CircleSlash aria-hidden="true" /> : ['repeat', 'needs_changes'].includes(stage.state) ? <RotateCcw aria-hidden="true" /> : <StageGlyph code={stage.kind} />}
          <small>{stage.position}</small></span>
        <span>{stageName(stage.code)}{stage.attempts.length > 1 && <small className="xo-work-attempts">{text('attempts', { count: stage.attempts.length })}</small>}</span>
        {stage.approvalRequirement === 'required' && <ShieldCheck className="xo-work-stage-gate" aria-label={text('approval_required')} />}
      </button>
    </li>)}</ol>
    {selected && <div className="xo-work-stage-detail">
      <div className="xo-work-stage-heading"><strong>{stageName(selected.code)}</strong><Pill tone={selected.state === 'completed' ? 'ok' : 'flat'}>{text(`state.${selected.state}`)}</Pill></div>
      {selected.state === 'needs_changes' && <p className="xo-work-rejection">{selected.attempts[selected.attempts.length - 1].reviewNote}</p>}
      {selected.state === 'repeat' && <p className="xo-work-help">{text('repeat_again')}</p>}
      {selected.state === 'awaiting_approval' && <p className="xo-work-help"><ShieldCheck aria-hidden="true" />{text('waiting_review')}</p>}
      {order.customer !== null && order.customer.afterStageId === selected.id && <CustomerSay customer={order.customer} />}
      {order.customer !== null && order.customer.waiting && current && selected.id === current.id && <CustomerSay customer={order.customer} />}
      {current && selected.id === current.id && order.canRecord && <LabResource resource={definition}>{data => {
        const stage = data.workflow.stages.find(item => item.id === selected.id);
        return <LaboratoryStageForm key={`${selected.id}-${selected.attempts.length}`} order={order} stage={stage}
          onRecorded={async () => { setSelectedId(null); await onChanged(); }} />;
      }}</LabResource>}
      <RecordHistory attempts={selected.attempts} open={selected.id !== (current && current.id)} />
    </div>}
  </section>;
}

function Review({ label, onReview }) {
  const [note, setNote] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const retry = useRef(null);
  async function decide(decision) {
    if (busy) return;
    if (decision === 'reject' && note.trim() === '') { setError(text('rejection_required')); return; }
    const signature = `${decision}:${note}`;
    if (retry.current === null || retry.current.signature !== signature) retry.current = { signature, requestId: crypto.randomUUID() };
    setBusy(true); setError('');
    try { await onReview({ requestId: retry.current.requestId, decision, ...(note.trim() ? { note } : {}) }); retry.current = null; }
    catch (caught) { setError(labError(caught)); }
    finally { setBusy(false); }
  }
  return <div className="xo-work-action">
    <label className="xo-work-note"><span>{text('review_note')}</span>
      <input value={note} onChange={event => setNote(event.target.value)} maxLength={2000} disabled={busy} aria-label={`${label} · ${text('review_note')}`} /></label>
    <div className="xo-work-action-buttons">
      <button className="xo-btn-2" type="button" onClick={() => decide('reject')} disabled={busy}><X aria-hidden="true" />{text('reject')}</button>
      <button className="xo-btn" type="button" onClick={() => decide('approve')} disabled={busy}><Check aria-hidden="true" />{text(busy ? 'saving' : 'approve')}</button>
    </div>
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </div>;
}

function Approvals() {
  const resource = useLiveResource(['samples'], loadLaboratoryApprovals);
  return <div className="xo-products-page xo-lab-work-page">
    <Head title={tx('ui.lab.approvals')} subtitle={text('approvals_subtitle')} />
    <LabResource resource={resource}>{data => data.stageRecords.length + data.acceptances.length === 0
      ? <Empty title={text('approvals_empty')} detail={text('approvals_empty_hint')} />
      : <div className="xo-work-collections">
        {data.stageRecords.map(item => <article className="xo-work-collection xo-approval" key={item.record.uuid}>
          <header><div><strong>{item.workOrder.workflow.code} · {stageName(item.stage.code)}</strong>
            <span><code>{item.workOrder.tube.printedCode ?? item.workOrder.tube.code}</code> · {text('attempt', { number: item.record.attemptNo })}</span></div>
            <Pill tone={item.record.outcome === 'pass' ? 'ok' : 'warn'}>{text(`outcome.${item.record.outcome}`)}</Pill></header>
          <p>{text('performed', { name: item.record.performedBy, at: labWhen(item.record.performedAt) })}</p>
          <dl className="xo-stage-values">{item.record.values.map(value => <div key={value.fieldId}><dt>{fieldLabel(value.code)}</dt><dd><RecordValue value={value} /></dd></div>)}</dl>
          {item.record.nextAction && item.record.nextAction.effect !== 'advance' && <p><strong>{tx(`ui.lab.next.${item.record.nextAction.code}`)}</strong></p>}
          {item.record.note && <blockquote>{item.record.note}</blockquote>}
          {item.record.mayReview ? <Review label={stageName(item.stage.code)} onReview={async body => { await reviewLaboratoryStage(item.record.uuid, body); await resource.refresh(); }} />
            : <p className="xo-work-help">{tx(`ui.lab.error.${item.record.reviewBlockedReason}`)}</p>}
        </article>)}
        {data.acceptances.map(item => <article className="xo-work-collection xo-approval" key={item.uuid}>
          <header><div><strong>{text('acceptance_of', { code: item.tube.code })}</strong><span>{sampleTypeName(item.sampleType.code)} · {tx(`ui.lab.accept.decision.${item.decision.code}`)}</span></div>
            <TubeState value="awaiting_approval" /></header>
          <p>{text('performed', { name: item.performedBy, at: labWhen(item.performedAt) })}</p>
          {item.mayReview ? <Review label={item.tube.printedCode ?? item.tube.code} onReview={async body => { await reviewLaboratoryAcceptance(item.uuid, body); await resource.refresh(); }} />
            : <p className="xo-work-help">{tx(`ui.lab.error.${item.reviewBlockedReason}`)}</p>}
        </article>)}
      </div>}</LabResource>
  </div>;
}

function Work() {
  const { permissions } = useXomicsAuth();
  const [params, setParams] = useSearchParams();
  const collectionUuid = params.get('collection');
  const { query, setQuery, search } = useLabSearch();
  const [status, setStatus] = useState('all'), [offset, setOffset] = useState(0), [selected, setSelected] = useState(null);
  const load = useCallback(() => loadLaboratoryWork({ query: search, limit: 50, offset, status,
    ...(collectionUuid === null ? {} : { collectionUuid }) }), [search, offset, status, collectionUuid]);
  const resource = useLiveResource(['samples'], load), data = resource.data;
  const current = data !== null && data.filters.query === query.trim() && data.offset === offset
    && data.filters.collectionUuid === collectionUuid && data.filters.status === status;
  const countsCurrent = data !== null && data.filters.query === query.trim() && data.filters.collectionUuid === collectionUuid;
  const change = setter => value => { setter(value); setOffset(0); };
  return <div className="xo-products-page xo-lab-work-page">
    <Head title={tx('ui.lab.work.title')} subtitle={text('work_subtitle')} />
    <div className="xo-product-navigation">
      <div className="xo-product-toolbar">
        <SearchField className="xo-product-search" value={query} onChange={change(setQuery)} maxLength={120} placeholder={text('search')} />
        <div className="xo-product-toolbar-actions">
          {collectionUuid !== null && <button className="xo-btn-2" type="button" onClick={() => { const next = new URLSearchParams(params); next.delete('collection'); setParams(next); setOffset(0); }}><X aria-hidden="true" />{tx('ui.lab.work.clear_collection')}</button>}
          <FilterMenu icon={SlidersHorizontal} label={text('scope')} value={status} onChange={change(setStatus)}
            options={['all', 'planned', 'in_progress', 'awaiting_approval', 'awaiting_customer', 'completed', 'stopped'].map(value => ({ value,
              label: text(`order.${value}`), count: countsCurrent ? data.statusCounts[value] : undefined }))} />
        </div>
      </div>
    </div>
    <section aria-busy={resource.loading || !current}>
      {resource.error ? <Failed message={resource.error} onRetry={resource.refresh} /> : !current ? <Loading label={tx('ui.lab.loading')} /> : <>
        <div className="xo-product-results-heading" role="status"><span>{tx(`ui.lab.inventory.${data.total === 1 ? 'collection' : 'collection_count'}`, { count: data.total })}</span></div>
        {data.collections.length === 0 ? <Empty title={text('work_empty')} detail={text('work_empty_hint')} />
          : <div className="xo-work-collections">{data.collections.map(collection => <article className="xo-work-collection" key={collection.uuid}>
            <header><button type="button" onClick={() => setSelected(collection)} className="xo-work-collection-link"><Workflow aria-hidden="true" />
              <span><code>{collection.printedCode ?? collection.code}</code><span>{ownSampleTypeName(collection.sampleType)}</span></span><ChevronRight aria-hidden="true" /></button>
              <div>{collection.product !== null && <span className="xo-work-product"><Package aria-hidden="true" />{collection.product.name}</span>}
                <span>{text(collection.workOrders.length === 1 ? 'one_order' : 'orders', { count: collection.workOrders.length })}</span></div>
            </header>
            {collection.workOrders.map(order => <WorkOrder key={order.uuid} order={order} collectionCode={collection.printedCode ?? collection.code} onChanged={resource.refresh} />)}
          </article>)}</div>}
        <LabPagination data={data} loading={resource.loading} onOffset={setOffset} />
      </>}
    </section>
    {selected !== null && <LaboratoryCollectionModal collectionUuid={selected.uuid} code={selected.code} onClose={() => setSelected(null)} onAssigned={resource.refresh}
      mayTrack={permissions.includes('SAMPLE_TRACK_READ') || permissions.includes('SAMPLE_TRACK_READ_ALL')} />}
  </div>;
}

export default function XomicsLabWork({ approvals = false }) {
  return approvals ? <Approvals /> : <Work />;
}
